import { type ReactNode, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { SHOW_PUBLIC_PRICING } from '../config/pricing-visibility'
import { useAuth } from '../lib/auth'
import { startCheckout } from '../lib/billing'
import { usePageMeta } from '../lib/meta'
import { PRICING_PRO_TAGLINE, PRICING_PRO_WHY } from '../lib/watch-lane-copy'

// Family sales desk — same address /account uses for API-key requests.
const SALES_MAILTO = 'mailto:sales@navo24.com?subject=FreightRatesMCP%20Pro%20pricing%20enquiry'

export function PricingPage() {
  usePageMeta(
    'Pricing — FreightRatesMCP',
    SHOW_PUBLIC_PRICING
      ? 'FreightRatesMCP pricing: free live rates and index, plus Pro rate alerts, full historical data, and a rates API + MCP server for AI agents from $49/mo.'
      : 'FreightRatesMCP pricing: free live rates and index forever. Pro adds unlimited lane alerts, full price history, and a rates API + MCP server, priced to your volume.',
    '/pricing',
    ['en']
  )
  const { session, isPro } = useAuth()
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  async function goPro() {
    if (!session) {
      navigate('/account')
      return
    }
    if (isPro) return
    setBusy(true)
    setNote(null)
    const { error } = await startCheckout()
    if (error) {
      setBusy(false)
      setNote(
        error === 'unavailable' || error === 'billing_not_configured'
          ? "Card checkout is switching on shortly. Sign in and we'll email you the moment it's live."
          : 'Could not start checkout. Please try again.'
      )
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-16">
      {/* Masthead */}
      <div className="mx-auto max-w-2xl text-center">
        <div className="mono text-xs uppercase tracking-[0.22em] text-signal">Pricing</div>
        <h1 className="mt-3 text-[clamp(2.2rem,5vw,3.4rem)] font-light leading-[1.03] tracking-[-0.04em]">
          Free to check. <span className="hl">Pro</span> to stay ahead.
        </h1>
        <p className="mt-4 text-[1.05rem] leading-relaxed text-muted">
          Live rates and the FreightRatesMCP Index are free forever. Pro adds unlimited lane alerts,
          full price history, and a rates API + MCP server for your stack and your agents.
        </p>
        <p className="mt-5 text-[1.05rem] font-medium leading-relaxed text-ink">
          Pro pays for itself on one container. A 40HC can swing four figures in a single week, so
          one well-timed booking, or one better-priced carrier, saves more than a year of Pro.
        </p>
      </div>

      {/* Tiers */}
      <div className="mx-auto mt-12 grid max-w-5xl items-stretch gap-5 lg:grid-cols-3">
        <Tier
          name="Free"
          price="$0"
          bill="Free forever"
          tagline="Check the market"
          features={[
            'Live lane rates & the FreightRatesMCP Index',
            'Rate trend + on-time reliability per lane',
            'Save & watch lanes · 1 rate alert',
            'Request a booking quote (RFQ)',
          ]}
          cta={
            <Link
              to="/rates"
              className="block rounded-xl border border-border-strong px-4 py-2.5 text-center font-semibold transition-colors hover:border-signal hover:text-signal"
            >
              Browse rates
            </Link>
          }
        />

        <Tier
          highlight
          name="Pro"
          price={SHOW_PUBLIC_PRICING ? '$49' : 'Talk to us'}
          per={SHOW_PUBLIC_PRICING ? '/mo' : undefined}
          bill={
            SHOW_PUBLIC_PRICING
              ? 'Billed monthly · cancel anytime'
              : 'Priced to your volume · cancel anytime'
          }
          tagline={PRICING_PRO_TAGLINE}
          why={PRICING_PRO_WHY}
          features={[
            'Everything in Free, plus —',
            'Unlimited rate alerts, by lane',
            'Full historical rate charts',
            'Weekly market-movers report',
            'Rates API + hosted MCP server',
            'Priority booking support',
          ]}
          cta={
            // With no public figure, the public CTA is a sales conversation, not a checkout. Existing
            // Pro customers still see their state, and the self-serve Stripe path stays live on
            // /account for anyone signed in.
            !SHOW_PUBLIC_PRICING && !isPro ? (
              <a
                href={SALES_MAILTO}
                rel="noopener"
                className="btn-accent block w-full rounded-xl px-4 py-3 text-center font-semibold"
              >
                Contact sales
              </a>
            ) : (
              <button
                type="button"
                onClick={goPro}
                disabled={busy || isPro}
                className="btn-accent block w-full rounded-xl px-4 py-3 text-center font-semibold disabled:cursor-default disabled:opacity-70"
              >
                {isPro
                  ? "You're on Pro ✓"
                  : busy
                    ? 'Opening…'
                    : session
                      ? 'Upgrade to Pro'
                      : 'Get Pro'}
              </button>
            )
          }
        />

        <Tier
          name="API & MCP"
          price="Custom"
          bill="Volume pricing"
          tagline="Rates in your stack & agents"
          features={[
            'Rate index + full historical API',
            'Hosted MCP server for AI agents',
            'Bulk lane benchmarks & webhooks',
            'Volume pricing & SLA',
          ]}
          cta={
            <Link
              to="/book"
              className="block rounded-xl border border-border-strong px-4 py-2.5 text-center font-semibold transition-colors hover:border-signal hover:text-signal"
            >
              Talk to us →
            </Link>
          }
        />
      </div>

      {note && <p className="mx-auto mt-6 max-w-xl text-center text-sm text-muted">{note}</p>}

      {/* Trust row */}
      <div className="mono mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] uppercase tracking-wide text-faint">
        <span>Free to browse, no card</span>
        <span aria-hidden="true">·</span>
        <span>Cancel anytime</span>
        <span aria-hidden="true">·</span>
        <span>Secure Stripe checkout</span>
        <span aria-hidden="true">·</span>
        <span>{SHOW_PUBLIC_PRICING ? 'Prices in USD' : 'Quoted in USD'}</span>
      </div>

      {/* FAQ */}
      <div className="mx-auto mt-20 max-w-2xl">
        <h2 className="text-center text-2xl font-semibold tracking-tight">Questions</h2>
        <dl className="mt-8 divide-y divide-border">
          {FAQ.map((f) => (
            <div key={f.q} className="py-5">
              <dt className="font-semibold">{f.q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-muted">{f.a}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-8 text-center text-sm text-muted">
          Already on Pro?{' '}
          <Link to="/account" className="font-medium text-link hover:underline">
            Manage your plan
          </Link>
          .
        </p>
      </div>
    </div>
  )
}

const FAQ = [
  {
    q: "What's free, exactly?",
    a: 'Live lane rates, the FreightRatesMCP Index, rate trend and on-time reliability per lane, one saved rate alert, and a booking quote (RFQ). Free forever, no card.',
  },
  {
    q: 'What does Pro add?',
    a: SHOW_PUBLIC_PRICING
      ? 'Unlimited rate alerts by lane, full historical rate charts, a weekly market-movers report, and the rates API + hosted MCP server. $49/mo, billed through Stripe, cancel anytime.'
      : 'Unlimited rate alerts by lane, full historical rate charts, a weekly market-movers report, and the rates API + hosted MCP server. Priced to the lanes and call volume you run: talk to us and you get a figure the same day. Billed through Stripe, cancel anytime.',
  },
  {
    q: 'What is the API / MCP server?',
    a: 'Programmatic access to the rate index and history, plus a hosted MCP server so your AI agents can pull live ocean rates directly. Custom / volume pricing: talk to us.',
  },
  {
    q: 'Do I need to pay to book?',
    a: 'No. Requesting a firm all-in quote from a vetted forwarder is free on any plan; checking rates and booking are never gated.',
  },
]

function Tier({
  name,
  price,
  per,
  bill,
  tagline,
  why,
  features,
  cta,
  highlight,
}: {
  name: string
  price: string
  per?: string
  bill?: string
  tagline: string
  why?: string
  features: string[]
  cta: ReactNode
  highlight?: boolean
}) {
  // Word prices ("Custom") read as a label, not a figure — size them a notch down (per the mockup).
  const wordPrice = !price.startsWith('$')
  return (
    <div
      className={`card flex h-full flex-col p-6 ${
        highlight ? 'border-signal/60 shadow-[0_30px_70px_-34px_rgb(var(--signal-rgb)/0.45)]' : ''
      }`}
    >
      {highlight && (
        <div className="mono mb-3 inline-block self-start rounded-full bg-signal-soft px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-signal">
          Most popular
        </div>
      )}
      <div className="text-xl font-bold">{name}</div>
      <div className="mt-1 text-sm text-muted">{tagline}</div>
      <div className="mt-5 flex items-baseline gap-1">
        <span
          className={`mono font-semibold tracking-tight tnum ${wordPrice ? 'text-3xl' : 'text-4xl'}`}
        >
          {price}
        </span>
        {per && <span className="mono text-sm text-faint">{per}</span>}
      </div>
      {/* Every tier gets one mono billing line — keeps the price blocks the same height. */}
      <div className="mono mt-1.5 min-h-[16px] text-[10.5px] uppercase tracking-wide text-faint">
        {bill}
      </div>
      {why && <p className="serif mt-3 text-[13px] italic text-muted">{why}</p>}
      <div className="my-5 h-px bg-border" />
      <ul className="flex-1 space-y-2.5 text-sm">
        {features.map((f, i) => (
          <li key={f} className="flex gap-2.5">
            {i === 0 && f.endsWith('—') ? (
              <span className="text-[13px] font-medium text-faint">{f}</span>
            ) : (
              <>
                <span className="mt-0.5 shrink-0 text-signal" aria-hidden="true">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-muted">{f}</span>
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-7">{cta}</div>
    </div>
  )
}
